/**
 * AIFutureRotatingFace — a "face" (one of the rotating tiles) inside the
 * homepage floating action stack. Sized to match its siblings (w-40 h-40
 * xl:w-48 xl:h-48) and follows the same absolute-positioned crossfade
 * pattern controlled by the parent's `tileIndex`.
 *
 * When THIS face is active, we also cycle through the 11 split-face
 * BEFORE/AFTER AI teasers every 3.5s so a client watching the tile sees
 * multiple treatment previews inside one 9-second dwell.
 *
 * Karine ask (Feb 6 2026): push AI Skin Analysis exposure hard — this
 * tile shares the same catalogue as the portal FutureSelfPeekCard and
 * the lobby TV signage.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { SPLIT_FACE_IMAGES } from "@/data/splitFaceImages";
import FacialVisionAILogo from "@/components/FacialVisionAILogo";

const INNER_ROTATE_MS = 3500;

const trackEvent = (name, meta = {}) => {
  try {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", name, meta);
    }
  } catch { /* noop */ }
};

export default function AIFutureRotatingFace({ active, language = "en", onOpen }) {
  const isFr = language === "fr";
  const [idx, setIdx] = useState(() =>
    Math.floor(Math.random() * SPLIT_FACE_IMAGES.length)
  );
  const activeRef = useRef(active);
  activeRef.current = active;

  // Preload every frame once — cheap and prevents flicker on first swap.
  useEffect(() => {
    SPLIT_FACE_IMAGES.forEach((h) => {
      const img = new Image();
      img.src = h.src;
    });
  }, []);

  // Advance the inner rotation only while THIS tile is on-screen. When the
  // parent flips `active` to false (tileIndex moves off 7) we pause so the
  // next visit starts on the current frame instead of skipping ahead.
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(() => {
      if (!activeRef.current) return;
      setIdx((i) => (i + 1) % SPLIT_FACE_IMAGES.length);
    }, INNER_ROTATE_MS);
    return () => clearInterval(t);
  }, [active]);

  const current = useMemo(() => SPLIT_FACE_IMAGES[idx], [idx]);
  const labelRight = current.labelRight[isFr ? "fr" : "en"];
  const tagline = current.tagline[isFr ? "fr" : "en"];

  const handleClick = () => {
    trackEvent("home_ai_future_click", {
      source: "homepage_rotating_tile",
      portrait_id: current.id,
    });
    if (onOpen) onOpen();
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-hidden={!active}
      tabIndex={active ? 0 : -1}
      className="absolute inset-0 block overflow-hidden shadow-2xl hover:shadow-[0_20px_60px_rgba(217,70,239,0.5)] transition-all duration-700 ease-in-out hover:scale-105 rounded-xl text-left"
      style={{
        opacity: active ? 1 : 0,
        pointerEvents: active ? "auto" : "none",
        border: "1.5px solid rgba(217,70,239,0.55)",
        background: "#0a0410",
      }}
      data-testid="floating-ai-future-face"
      aria-label={isFr ? "Voyez votre futur avec l'IA" : "See your future with AI"}
    >
      {/* Full-bleed rotating split-face image (all frames stacked; opacity swap) */}
      {SPLIT_FACE_IMAGES.map((img, i) => (
        <img
          key={img.id}
          src={img.src}
          alt={img.alt}
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[900ms] ease-in-out"
          style={{
            opacity: i === idx ? 1 : 0,
            objectPosition: "center 22%",
          }}
        />
      ))}

      {/* Center gold mirror line — visual cue for "before | after" */}
      <span
        aria-hidden
        className="absolute inset-y-4 left-1/2 -translate-x-1/2 w-[1px] pointer-events-none"
        style={{ background: "linear-gradient(to bottom, transparent, rgba(223,192,110,0.6), transparent)" }}
      />

      {/* AFTER pill in the top-right corner (mirrors the portal card) */}
      <span
        className="absolute top-1.5 right-1.5 text-[8.5px] font-extrabold uppercase tracking-widest px-1.5 py-0.5 rounded-full"
        style={{
          background: "linear-gradient(135deg, #f472b6, #a855f7)",
          color: "#fff",
          boxShadow: "0 2px 6px rgba(168,85,247,0.5)",
        }}
        data-testid="ai-future-face-label-right"
      >
        {labelRight}
      </span>

      {/* Bottom scrim — matches the sibling faces' bottom-panel layout */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 p-2 text-center">
        <div className="flex items-center justify-center h-4">
          <FacialVisionAILogo size="sm" variant="mono-white" />
        </div>
        <span
          className="block text-white text-[11px] font-bold leading-tight mt-0.5"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          {isFr ? "Voyez-vous dans le futur" : "See Yourself in the Future"}
        </span>
        <span
          className="block text-[9px] mt-0.5 italic leading-tight px-1 truncate"
          style={{ color: "rgba(240,171,252,0.9)" }}
          data-testid="ai-future-face-tagline"
        >
          {tagline}
        </span>
        <span
          className="mt-1 inline-block text-[9px] uppercase tracking-[1.5px] px-2 py-0.5 rounded-full"
          style={{
            background: "linear-gradient(145deg, #f472b6, #a855f7)",
            color: "#fff",
            fontWeight: 700,
          }}
        >
          {isFr ? "Cliquez ici →" : "Click Here →"}
        </span>
      </div>

      {/* Hover shimmer sweep */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/20 to-transparent"
      />
    </button>
  );
}
